import { apiFetch } from './client'

export const seedBracket = (secret: string) =>
  apiFetch<{ ok: boolean; seeded: number }>('/admin/matches/seed', {
    method: 'POST',
    adminSecret: secret,
  })

export const finalizeR32 = (secret: string) =>
  apiFetch<{ ok: boolean; teams: number; matches: number; changed: number }>('/admin/bracket/finalize-r32', {
    method: 'POST',
    adminSecret: secret,
  })

export const assignTeams = (
  assignments: Array<{ matchId: string; homeTeamId: string; awayTeamId: string; scheduledAt?: number }>,
  secret: string,
  confirm = false
) =>
  apiFetch<{ ok: boolean; changed: number; predictionsCleared: number }>('/admin/matches/assign-teams', {
    method: 'POST',
    body: JSON.stringify({ assignments, confirm }),
    adminSecret: secret,
  })

export const setMatchResult = (
  matchId: string,
  homeScore: number,
  awayScore: number,
  winnerId: string,
  secret: string
) =>
  apiFetch<{ ok: boolean }>(`/admin/matches/${matchId}/result`, {
    method: 'PUT',
    body: JSON.stringify({ homeScore, awayScore, winnerId }),
    adminSecret: secret,
  })

export const correctMatch = (
  matchId: string,
  homeTeamId: string,
  awayTeamId: string,
  homeScore: number,
  awayScore: number,
  winnerId: string,
  secret: string
) =>
  apiFetch<{ ok: boolean; removedTeams: string[]; predictionsPreservedFor: string[] }>(
    `/admin/matches/${matchId}/correct`,
    {
      method: 'POST',
      body: JSON.stringify({ homeTeamId, awayTeamId, homeScore, awayScore, winnerId }),
      adminSecret: secret,
    }
  )

export const triggerSync = (secret: string) =>
  apiFetch<{ ok: boolean; updated: number }>('/admin/sync', {
    method: 'POST',
    adminSecret: secret,
  })

export const getSyncStatus = (secret: string) =>
  apiFetch<{ lastSyncAt: number | null }>('/admin/sync/status', { adminSecret: secret })

export const getBracketTemplate = (secret: string) =>
  apiFetch<object>('/admin/bracket/template', { adminSecret: secret })

export const importAiPlayer = (
  displayName: string,
  predictions: Array<{
    match_id: string
    predicted_winner_id: string
    predicted_home_score?: number | null
    predicted_away_score?: number | null
  }>,
  secret: string
) =>
  apiFetch<{ ok: boolean; playerId: string; saved: number }>('/admin/ai-player', {
    method: 'POST',
    body: JSON.stringify({ displayName, predictions }),
    adminSecret: secret,
  })

export const getPlayers = (secret: string) =>
  apiFetch<object[]>('/admin/players', { adminSecret: secret })

export const removePlayer = (id: string, secret: string) =>
  apiFetch<{ ok: boolean }>(`/admin/players/${id}`, {
    method: 'DELETE',
    adminSecret: secret,
  })

export const getAdminSettings = (secret: string) =>
  apiFetch<Record<string, string>>('/admin/settings', { adminSecret: secret })

export const updateAdminSettings = (settings: Record<string, string>, secret: string) =>
  apiFetch<{ ok: boolean }>('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
    adminSecret: secret,
  })

export const recomputeScores = (secret: string) =>
  apiFetch<{ ok: boolean; matchesScored: number }>('/admin/scores/recompute', {
    method: 'POST',
    adminSecret: secret,
  })

export const createInviteCode = (code: string, maxUses: number | null, secret: string, groupId?: string) =>
  apiFetch<{ ok: boolean; code: string }>('/admin/invite-codes', {
    method: 'POST',
    body: JSON.stringify({ code, maxUses, groupId }),
    adminSecret: secret,
  })

export interface AdminGroup {
  id: string
  name: string
  created_at: number
  player_count: number
  invite_codes: Array<{ code: string; max_uses: number | null; use_count: number }>
}

export const getAdminGroups = (secret: string) =>
  apiFetch<AdminGroup[]>('/admin/groups', { adminSecret: secret })

export const createAdminGroup = (name: string, secret: string) =>
  apiFetch<{ ok: boolean; id: string; name: string }>('/admin/groups', {
    method: 'POST',
    body: JSON.stringify({ name }),
    adminSecret: secret,
  })

export const renameAdminGroup = (id: string, name: string, secret: string) =>
  apiFetch<{ ok: boolean }>(`/admin/groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
    adminSecret: secret,
  })

export const deleteAdminGroup = (id: string, secret: string) =>
  apiFetch<{ ok: boolean }>(`/admin/groups/${id}`, {
    method: 'DELETE',
    adminSecret: secret,
  })

export interface GroupPlayer {
  id: string
  display_name: string
  is_ai: number
  email: string | null
  created_at: number
}

export const getGroupPlayers = (groupId: string, secret: string) =>
  apiFetch<GroupPlayer[]>(`/admin/groups/${groupId}/players`, { adminSecret: secret })

export const removeGroupPlayer = (groupId: string, playerId: string, secret: string) =>
  apiFetch<{ ok: boolean }>(`/admin/groups/${groupId}/players/${playerId}`, {
    method: 'DELETE',
    adminSecret: secret,
  })

export const getAdminTeams = (secret: string) =>
  apiFetch<Array<{ id: string; name: string; flag_emoji: string | null; group_label: string | null }>>('/admin/teams', { adminSecret: secret })

export interface StandingsGroup {
  group: string
  table: Array<{ position: number; tla: string; name: string; played: number; points: number; gd: number; gf: number }>
}

export interface ProposedAssignment {
  matchId: string
  homeTeamId: string | null
  awayTeamId: string | null
  homeLabel: string
  awayLabel: string
}

export const getStandings = (secret: string) =>
  apiFetch<{ standings: StandingsGroup[]; assignments: ProposedAssignment[] }>('/admin/standings', { adminSecret: secret })
